import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import { ContractTabs } from 'components/organisms/forms/contract/ContractForm';
import { useContractParamIds, useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  useActivateContract,
  useGetContract,
  useUpdateContract,
  useWithdrawContract,
} from 'services/contract';
import { CONTRACT_STATUS } from 'services/contract/types';
import { IContractTemplateForm } from 'services/templates/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { contractTemplateUtil } from 'utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { contractInitialValues } from '../create/CreateContract';

const EditContract = () => {
  const labelId = useParamId('/label');
  const contractId = useContractParamIds(`/label/${labelId}/contract`);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const contract = useGetContract(contractId, {
    notFoundPath: `/label/${labelId}/contract`,
  });

  const activateContract = useActivateContract();

  const withdrawContract = useWithdrawContract();

  const updateContract = useUpdateContract();

  const goBackToContractDetail = () =>
    navigate(`/label/${labelId}/contract/${contractId}/form`);

  const updateContractStatus = async (values: IContractTemplateForm) => {
    const status = values.details?.status;

    if (contract.data?.status !== status) {
      status === CONTRACT_STATUS.active &&
        (await activateContract.mutateAsync(contractId, {
          onSuccess: goBackToContractDetail,
          onError: error => onErrorShowAllErrorsToast(error),
        }));
      status === CONTRACT_STATUS.withdraw &&
        (await withdrawContract.mutateAsync(contractId, {
          onSuccess: goBackToContractDetail,
          onError: error => onErrorShowAllErrorsToast(error),
        }));
    }

    goBackToContractDetail();
  };

  const onUpdateContract = async (values: IContractTemplateForm) => {
    const updatedContract = middleware.contract.formToQuery(values);

    if (
      !updatedContract.digitalDistributionSection &&
      !updatedContract.merchDistributionSection &&
      !updatedContract.channelDistributionSection &&
      !updatedContract.physicalDistributionSection
    ) {
      return toast.error(t('formik-error.at-least-one-distribution'));
    }

    await updateContract.mutateAsync(
      { ...updatedContract, contractId },
      {
        onSuccess: async () => {
          await updateContractStatus(values);
        },
        onError: error => onErrorShowAllErrorsToast(error),
      },
    );
  };

  return (
    <Form.Contract
      customBackItem={
        <Button type='button' onlyIcon onClick={goBackToContractDetail}>
          <Button.Description description={t('button.cancel')} />

          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <>
          <Button type='submit' variant={BUTTON_VARIANT.submit}>
            <Button.Text text={t(`button.save`)} />
          </Button>
        </>
      }
      initialStatus={{
        ...contractTemplateUtil.contract.initialStatus
      }}
      initialValues={
        contract.data
          ? middleware.contract.queryToForm(contract.data)
          : contractInitialValues
      }
      withTabs
      disabledTabs={[
        ContractTabs.products,
        ContractTabs.tracks,
        ContractTabs.videos,
        ContractTabs.channelVideos,
      ]}
      onSubmit={onUpdateContract}
    />
  );
};

export default EditContract;
